@defer (when wasDataLoadedOnce$ | async) {
  <div class="flex flex-col flex-1 min-h-0 m-4 mt-8">
    <div class="mx-4">
      <span class="flex items-center gap-1.5 relative">
        <div class="text-navy-900 font-semibold text-lg">
          {{ (COR$ | async)?.title }}
        </div>
        <app-styled-options-list
          [listItems]="editOptions"
          [disabledActionList]="
            COR.status === 'approved' || COR.status === 'rejected' ? [OPTIONS.MARK_APPROVED, OPTIONS.MARK_REJECTED] : []
          "
          (selectedOption)="registerOption($event)"
        >
          <span class="icon-options-inverted cursor-pointer text-blue-700 flex items-center"></span>
        </app-styled-options-list>
      </span>
      <div class="text-navy-900 font-normal text-sm">{{ (COR$ | async).number }}</div>
    </div>

    <div class="mt-6 border-b border-b-solid border-navy-300"></div>

    <div class="m-4 my-2 flex justify-between items-center gap-2 min-h-10">
      <div class="w-10/12 text-ellipsis line-clamp-2 flex items-center">
        {{ (COR$ | async)?.description ?? 'Missing description.' }}
      </div>
      <div class="flex gap-2 items-center">
        @if (COR?.files?.length > 0) {
          <app-attachments-dropdown
            [files]="COR.files"
            fileCardClass="h-[24px]"
            iconSize="text-size_xl"
            [zipFileName]="COR.number + '_attachments'"
          ></app-attachments-dropdown>
        }
        <div class="w-2/12 flex items-center justify-center">
          <app-status-block class="w-full" [status]="(COR$ | async).status"></app-status-block>
        </div>
      </div>
    </div>

    <div class="border-b border-b-solid border-navy-300"></div>

    <app-dynamic-projected-table
      class="block"
      [isEmpty]="false"
      [wasDataLoadedOnce]="wasDataLoadedOnce$ | async"
      [isLoading]="isLoading$ | async"
    >
      <div tableHeader class="grid-row header-row bg-navy-100">
        <div class="item font-semibold text-sm text-navy-900">Line Item & Description</div>
        <div></div>
        <div class="item font-semibold text-sm text-navy-900">Commitment Dollars</div>
      </div>

      <!-- PCO level-->
      <div projectedTable class="grid-table">
        @for (pco of COR?.pcos; track pco.id) {
          <app-expansion-panel
            [expanded]="expandedPCOs.get(pco.id)"
            (expandedChange)="expandedPCOs.set(pco.id, $event)"
          >
            <div header-row class="grid-row header-row">
              <div class="item flex items-center">
                @if (expandedPCOs.get(pco.id)) {
                  <span class="icon-expand-square mr-1"></span>
                } @else {
                  <span class="icon-collapse-square mr-1"></span>
                }

                <div class="item flex flex-col px-1">
                  <span class="font-semibold text-sm">
                    {{ pco.number }}
                  </span>
                  <span class="font-normal text-xs">{{ pco?.title }}</span>
                </div>
              </div>

              <span class="vertical-separator"></span>

              <div [class.negative]="pco.commitment_dollars < 0" class="item text-sm font-semibold">
                {{ pco.commitment_dollars | money: 'positiveComa' }}
              </div>

              <span class="vertical-separator"></span>

              <div class="item flex justify-start">{{ pco.duration_extension }} day duration extension</div>
            </div>

            <!-- SOV level-->
            <div content-row>
              @for (sov of pco.sovs; track sov.id) {
                <app-expansion-panel
                  class="pco-mapping-parent"
                  [expanded]="expandedSOVs.get(pco.id + '_' + sov.id)"
                  (expandedChange)="expandedSOVs.set(pco.id + '_' + sov.id, $event)"
                >
                  <div header-row class="grid-row header-row">
                    <div class="item flex items-center pl-4">
                      @if (expandedSOVs.get(pco.id + '_' + sov.id)) {
                        <span class="icon-expand-square mr-1"></span>
                      } @else {
                        <span class="icon-collapse-square mr-1"></span>
                      }

                      <div class="item flex flex-col px-1">
                        <app-faded-text [displayGrid]="true" class="font-semibold text-sm"
                          >{{ sov.name }}
                        </app-faded-text>
                      </div>
                    </div>

                    <span class="vertical-separator"></span>

                    <div [class.negative]="sov.commitment_dollars < 0" class="item text-sm font-normal">
                      {{ sov.commitment_dollars | money: 'positiveComa' }}
                    </div>

                    <span class="vertical-separator"></span>
                  </div>

                  <!-- PCO Item level-->
                  <div content-row>
                    @for (pcoItem of sov.pco_items; track pcoItem.id) {
                      <app-expansion-panel
                        class=""
                        [expanded]="expandedPCOItems.get(pcoItem.id)"
                        (expandedChange)="expandedPCOItems.set(pcoItem.id, $event)"
                      >
                        <div header-row class="grid-row header-row">
                          <div class="item flex items-center pl-6">
                            @if (expandedPCOItems.get(pcoItem.id)) {
                              <span class="icon-expand-square mr-1"></span>
                            } @else {
                              <span class="icon-collapse-square mr-1"></span>
                            }

                            <div class="item flex flex-col px-1">
                              <app-faded-text [displayGrid]="true" class="font-semibold text-sm"
                                >{{ pcoItem.item_name }}
                              </app-faded-text>
                              <app-faded-text [displayGrid]="true" class="font-normal text-sm">
                                {{ pcoItem.division }} {{ pcoItem.cost_type }}
                              </app-faded-text>
                            </div>
                          </div>

                          <span class="vertical-separator"></span>

                          <div [class.negative]="pcoItem.commitment_dollars < 0" class="item text-sm font-normal">
                            {{ pcoItem.commitment_dollars | money: 'positiveComa' }}
                          </div>

                          <span class="vertical-separator"></span>
                        </div>

                        <!-- ------------------------ -->
                        <!-- PCO Item Cost level-->
                        <div content-row>
                          @for (pcoItemCost of pcoItem.pco_item_costs; track pcoItemCost.id) {
                            <div class="grid-row">
                              <div class="item flex px-1">
                                <i class="icon-rectangle text-size_m text-navy-400 ml-7 mr-3"></i>
                                <div class="item">
                                  <app-faded-text [displayGrid]="true" class="font-normal text-sm"
                                    >{{ pcoItemCost.spend_description }}
                                  </app-faded-text>
                                </div>
                              </div>

                              <span class="vertical-separator"></span>

                              <div
                                [class.negative]="pcoItemCost.commitment_dollars < 0"
                                class="item text-sm font-normal"
                              >
                                {{ pcoItemCost.commitment_dollars | money: 'positiveComa' }}
                              </div>

                              <span class="vertical-separator"></span>
                            </div>
                          } @empty {
                            <span
                              class="border-b border-solid border-b-navy-300 flex flex-col items-center font-semibold text-navy-500 p-4"
                              >There are no PCO item costs.</span
                            >
                          }
                        </div>
                      </app-expansion-panel>
                    } @empty {
                      <span
                        class="border-b border-solid border-b-navy-300 flex flex-col items-center font-semibold text-navy-500 p-4"
                        >There are no PCO items.</span
                      >
                    }
                  </div>
                </app-expansion-panel>
              } @empty {
                <span
                  class="border-b border-solid border-b-navy-300 flex flex-col items-center font-semibold text-navy-500 p-4"
                  >There are no SOVs.</span
                >
              }

              <app-cor-markups [PCO]="pco"></app-cor-markups>
            </div>
          </app-expansion-panel>
        } @empty {
          <span
            class="border-b border-solid border-b-navy-300 flex flex-col items-center font-semibold text-navy-500 p-4"
            >There are no PCOs available.</span
          >
        }
      </div>

      <div tableFooter class="grid-row totals bg-navy-100">
        <div class="item font-semibold">{{ (COR$ | async).number }} Total</div>
        <span class="vertical-separator"></span>
        <div
          [class.negative]="(combinedCommitmentTotals$ | async) < 0"
          class="item font-semibold flex justify-center gap-1"
        >
          {{ combinedCommitmentTotals$ | async | money: 'positiveComa' }}
        </div>
        <span class="vertical-separator"></span>
        <div class="item text-sm font-normal flex justify-start items-center gap-1">
          {{ totalDaysExtension$ | async }} day duration extension
        </div>
      </div>
    </app-dynamic-projected-table>
  </div>
} @placeholder {
  <app-page-loading></app-page-loading>
}
